import { yupResolver } from '@hookform/resolvers/yup'
import Axios from 'axios'
import cookie from 'js-cookie'
import { Trans, useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { RegistrationRequest } from '@fe/common/api/models/RequestModels/RegistrationRequest'
import { GenderEnum } from '@fe/common/api/models/enums/GenderEnum'
import { Button } from '@fe/common/components/Button'
import { Separator } from '@fe/common/components/Separator'
import { Checkbox } from '@fe/common/components/inputs/Checkbox'
import { DatePicker } from '@fe/common/components/inputs/DatePicker'
import { Input } from '@fe/common/components/inputs/Input'
import { RadioPicker } from '@fe/common/components/inputs/RadioPicker'
import {
  BottomLink,
  BottomOptions,
} from '@fe/common/components/loggedOutStyles'
import { ErrorText } from '@fe/common/components/typography/ErrorText'
import {
  GDPR_HAS_AGREED_COOKIE_KEY,
  REFERRAL,
} from '@fe/common/constants/cookies'
import { SCREEN, FONT_SIZE, COLOR } from '@fe/common/constants/main'
import { getGenderOptions } from '@fe/common/constants/options'
import { formatResponseError } from '@fe/common/utils/axiosUtils'
import { removeCookie } from '@fe/common/utils/cookies'
import { REFERRAL_NAME, rercordEvent } from '@fe/common/utils/gaUtils'
import yup from '@fe/common/yup/yupConfig'
import {
  FintwinSignupConsents,
  FintwinSignupData,
  fintwinSignupShape,
  isDetailRequired,
  LegalNotice,
} from './FintwinSignupConsents'
import { RegistrationApi } from 'src/api/RegistrationApi'
import { LOGIN } from 'src/constants/routes'
import { isFintwin } from 'src/state/selectors/fintwinSelectors'
// import { useRecaptcha } from '@fe/common/hooks/useRecaptcha'
import {
  isCryptoSelector,
  isFiatSelector,
} from 'src/state/selectors/globalDataSelectors'

const SignUpForm = styled.form`
  ${SCREEN.ABOVE_MOBILE} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 0 2rem;
    margin-bottom: 0;
  }
`

const StyledSeparator = styled(Separator)`
  grid-column: 1/3;
`

const AgreementsContainer = styled.div`
  grid-column: 1/3;
  margin: 3rem 0 4rem;
`

const PasswordFormatTip = styled.p`
  grid-column: 1/3;
  max-width: 60rem;
  color: ${COLOR.OFF_BLACK};
  font-size: ${FONT_SIZE.EXTRA_SMALL};
`

const TermsCheckbox = styled(Checkbox)`
  font-size: ${FONT_SIZE.SMALL};
  white-space: nowrap;

  span {
    flex-wrap: wrap;
    max-width: 500px;
  }

  ${SCREEN.ABOVE_MOBILE} {
    font-size: ${FONT_SIZE.BASE};
  }
`

const SubmitButton = styled(Button)`
  width: 100%;
  margin: auto 0;

  ${SCREEN.ABOVE_MOBILE} {
    grid-column: 1/3;
    width: fit-content;
  }
`

const StyledErrorText = styled(ErrorText)`
  position: absolute;
  bottom: 1.5rem;
  left: 0rem;

  ${SCREEN.ABOVE_MOBILE} {
    bottom: 5.5rem;
    left: 17rem;
    width: fit-content;
  }
`

// FINTWIN renders a legal notice under the button, so the error cannot be
// pinned to the bottom of the card there - it flows under the button instead.
const InlineErrorText = styled(ErrorText)`
  grid-column: 1/3;
  margin-top: 1.5rem;
`

const baseSignupShape = {
  first_name: yup.string().trim().required(),
  last_name: yup.string().trim().required(),
  birth_date: yup.date().birthDate().required(),
  email: yup.string().email().trim().required(),
  password: yup.string().password().required(),
  repeatPassword: yup.string().repeatPassword('password').required(),
  gender: yup.string().gender().required(),
  // terms: yup.boolean().required(),
  // privacy: yup.boolean(),
  // framework: yup.boolean(),
}

const signupSchema = yup.object().shape(baseSignupShape)

// FINTWIN adds the source question and three mandatory confirmations.
const fintwinSignupSchema = yup
  .object()
  .shape({ ...baseSignupShape, ...fintwinSignupShape })

export interface SignupData extends Partial<FintwinSignupData> {
  first_name: string
  last_name: string
  birth_date: Date
  email: string
  password: string
  repeatPassword: string
  gender: GenderEnum
  // terms: boolean
  // privacy: boolean
  // framework: boolean
  gdprCookie: unknown
}

interface FormStepProps {
  incrementStep: () => void
}

export const FormStep: React.FC<FormStepProps> = ({ incrementStep }) => {
  const { t } = useTranslation('no-auth')
  const [error, setError] = useState('')

  const isFintwinBrand = isFintwin()

  const { register, handleSubmit, errors, formState, control, trigger, watch } =
    useForm<SignupData>({
      mode: 'onBlur',
      resolver: yupResolver(
        isFintwinBrand ? fintwinSignupSchema : signupSchema
      ),
    })

  // const { ReCaptcha, checkCaptcha } = useRecaptcha('register')
  // const isTermsChecked = watch('terms')
  // const isPrivacyChecked = watch('privacy')
  // const isFrameworkChecked = watch('framework')
  const isTermsChecked = true
  const isPrivacyChecked = true
  const isFrameworkChecked = true
  const axiosCancelSource = useRef(Axios.CancelToken.source())

  useEffect(() => () => axiosCancelSource.current.cancel(), [])

  const onSubmit = async (formData: SignupData) => {
    if (!cookie.get(GDPR_HAS_AGREED_COOKIE_KEY)) {
      setError(t('cookie-warning'))

      return
    }

    const { consent_terms, source_detail, ...formattedFormData } = formData
    delete formattedFormData.repeatPassword

    const requestData: RegistrationRequest = {
      ...formattedFormData,
      gdpr_consent: true, // formattedFormData.terms, // one check for both
      terms: true, // hardcoded since checkbox is disabled
    }

    if (isFintwinBrand) {
      // One real checkbox covers both (Website Terms of Use + Privacy Policy).
      requestData.terms = !!consent_terms
      requestData.gdpr_consent = !!consent_terms

      if (isDetailRequired(formData.source_code)) {
        requestData.source_detail = source_detail
      }
    }

    const referralData = cookie.get(REFERRAL)

    if (referralData) {
      const [referral_id, referral_hash] = referralData.split(':')

      requestData.referral_id = Number(referral_id)
      requestData.referral_hash = referral_hash
    }

    try {
      // await checkCaptcha()

      await RegistrationApi.register(requestData, {
        cancelToken: axiosCancelSource.current.token,
      })

      rercordEvent('sign_up', {
        promotions: [{ id: referralData, name: REFERRAL_NAME }],
      })
      removeCookie(REFERRAL)
      incrementStep()
    } catch (error) {
      setError(formatResponseError(error, t))
    }
  }

  const isCrypto = useSelector(isCryptoSelector)
  const isFiat = useSelector(isFiatSelector)

  const fintwinGate = watch([
    'source_code',
    'consent_terms',
    'attestation_own_initiative',
    'attestation_canada_notice',
  ])

  const isLegacySubmitEnabled = isCrypto
    ? isTermsChecked
    : isTermsChecked && isPrivacyChecked && isFrameworkChecked

  const isSubmitEnabled = isFintwinBrand
    ? !!fintwinGate.source_code &&
      !!fintwinGate.consent_terms &&
      !!fintwinGate.attestation_own_initiative &&
      !!fintwinGate.attestation_canada_notice
    : isLegacySubmitEnabled

  return (
    <>
      <SignUpForm onSubmit={handleSubmit(onSubmit)}>
        <Input
          name="first_name"
          label={t('common:first-name')}
          placeholder={t('common:first-name')}
          isFocused
          errors={errors}
          formState={formState}
          ref={register}
        />
        <Input
          name="last_name"
          label={t('common:last-name')}
          placeholder={t('common:last-name')}
          errors={errors}
          formState={formState}
          ref={register}
        />

        <DatePicker
          name="birth_date"
          label={t('common:date-of-birth')}
          isAge
          control={control}
          errors={errors}
          formState={formState}
        />

        <RadioPicker
          name="gender"
          options={getGenderOptions(t)}
          ref={register}
          errors={errors}
          formState={formState}
        />

        <StyledSeparator />

        <Input
          name="password"
          type="password"
          onBlur={() => trigger('repeatPassword')}
          errors={errors}
          formState={formState}
          ref={register}
        />

        <Input
          name="repeatPassword"
          label={t('common:repeat-password')}
          placeholder="Repeat password"
          type="password"
          errors={errors}
          formState={formState}
          ref={register}
        />

        <Input
          name="email"
          type="email"
          errors={errors}
          formState={formState}
          ref={register}
        />

        <PasswordFormatTip>
          {t('common:password-format-warning')}
        </PasswordFormatTip>

        {isFintwinBrand && (
          <FintwinSignupConsents
            register={register}
            control={control}
            errors={errors}
            formState={formState}
            sourceCode={fintwinGate.source_code}
          />
        )}

        <AgreementsContainer>
          {false && isCrypto && (
            <TermsCheckbox
              name="terms"
              label={
                <Trans i18nKey="crypto-agreement" t={t}>
                  I agree with the
                  <a
                    href="/docs/terms.pdf"
                    rel="noopener nonrefferer"
                    target="_blank"
                  >
                    Terms of Service and Privacy Policy
                  </a>
                </Trans>
              }
              ref={register}
            />
          )}
          {false && isFiat && (
            <>
              {/* Terms of Service and Privacy Policy Checkbox */}
              <TermsCheckbox
                name="terms"
                label={
                  <Trans i18nKey="fiat-agreement-terms" t={t}>
                    I agree with the
                    <a
                      href="/docs/terms.pdf"
                      rel="noopener nonrefferer"
                      target="_blank"
                    >
                      Terms of Service
                    </a>
                  </Trans>
                }
                ref={register}
              />

              {/* Privacy Checkbox */}
              <TermsCheckbox
                name="privacy"
                label={
                  <Trans i18nKey="fiat-agreement-privacy" t={t}>
                    I agree with the
                    <a
                      href="/docs/privacy.pdf"
                      rel="noopener nonrefferer"
                      target="_blank"
                    >
                      Privacy Policy
                    </a>
                  </Trans>
                }
                ref={register}
              />

              {/* Framework Agreement Checkbox */}
              <TermsCheckbox
                name="framework"
                label={
                  <Trans i18nKey="fiat-agreement-framework" t={t}>
                    I agree with the
                    <a
                      href="/docs/framework.pdf"
                      rel="noopener nonrefferer"
                      target="_blank"
                    >
                      Framework Agreement
                    </a>{' '}
                    and the
                    <a
                      href="/docs/pricelist.pdf"
                      rel="noopener nonrefferer"
                      target="_blank"
                    >
                      Pricelist
                    </a>
                  </Trans>
                }
                ref={register}
              />
            </>
          )}
        </AgreementsContainer>
        <SubmitButton
          isSubmit
          isDisabled={!isSubmitEnabled}
          isLoading={formState.isSubmitting}
          testId="continue_button"
        >
          {t('common:continue')}
        </SubmitButton>

        {error &&
          (isFintwinBrand ? (
            <InlineErrorText>{error}</InlineErrorText>
          ) : (
            <StyledErrorText>{error}</StyledErrorText>
          ))}

        {isFintwinBrand && (
          <LegalNotice data-test-id="signup_legal_notice">
            {t('signup-legal-notice')}
          </LegalNotice>
        )}

        <BottomOptions>
          <Link href={LOGIN} passHref>
            <BottomLink data-test-id="sign_in_link">{t('sign-in')}</BottomLink>
          </Link>
        </BottomOptions>
      </SignUpForm>

      {/* <ReCaptcha /> */}
    </>
  )
}
